import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { getAtsJobCreate } from '@/store/atsJobCreate';
import { SURVEY_API_SERVICES } from '../api';
import type {
  UseSurveyTooltipProps,
  UseSurveyTooltipReturn,
  FilterBy,
  SurveyData,
} from './useSurveyTooltip.interface';

export const useSurveyTooltip = ({
  enabled = false,
}: UseSurveyTooltipProps = {}): UseSurveyTooltipReturn => {
  const { currentJob } = useSelector(getAtsJobCreate);
  const currentJobData = currentJob?.data || {};
  const { department, office, employment_type } =
    currentJobData?.basic_info || {};

  const filter_by = useMemo<FilterBy | undefined>(() => {
    if (!currentJobData?.basic_info) return undefined;

    const params: FilterBy = {};

    if (department?.id) {
      params.department_ids = {
        is: [department.id],
      };
    }

    if (office?.length) {
      params.office_ids = {
        is: office.map((itr: any) => itr.id),
      };
    }

    if (employment_type) {
      params.employment_types = {
        is: [employment_type],
      };
    }

    params.status = {
      is: ['published'],
    };

    return params;
  }, [currentJobData?.basic_info, department, office, employment_type]);

  const { data: surveyData, isLoading: isSurveyLoading } = useQuery<{
    data: SurveyData;
  }>({
    queryKey: ['survey', 'form-templates', filter_by],
    queryFn: () =>
      SURVEY_API_SERVICES.getSurveyList(filter_by ? { filter_by } : undefined),
    enabled: !!enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  const surveyFormTemplates = surveyData?.data?.survey_form_templates || [];
  const hasNoSurveys =
    enabled && !isSurveyLoading && surveyFormTemplates.length === 0;

  return {
    surveyFormTemplates,
    hasNoSurveys,
    isSurveyLoading,
  };
};
